import { adminUserTypes, UserTypes } from "../constants/constants";
import * as XLSX from 'xlsx';

export const isAdminUser = (userType) => {
    return adminUserTypes.includes(userType);
}

export const isOfficeUser = (userType) => {
    return userType === UserTypes.OFFICE;
}

/**
 * Validates if a file is a valid CSV file
 * @param {File} file - File object to validate
 * @returns {boolean} True if valid CSV file
 */
export const isValidCSVFile = (file) => {
    if (!file) return false;
    
    // Check file extension
    const fileName = file.name.toLowerCase();
    const validExtensions = ['.csv'];
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    
    // Check MIME type
    const validMimeTypes = ['text/csv', 'application/csv', 'text/plain'];
    const hasValidMimeType = validMimeTypes.includes(file.type);
    
    // File size check (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    const isValidSize = file.size <= maxSize;
    
    return hasValidExtension && (hasValidMimeType || file.type === '') && isValidSize;
}

/**
 * Validates if a file is a valid spreadsheet file (CSV, XLSX, XLS)
 * @param {File} file - File object to validate
 * @returns {boolean} True if valid spreadsheet file
 */
export const isValidSpreadsheetFile = (file) => {
    if (!file) return false;

    const fileName = file.name.toLowerCase();
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

    // File size check (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    const isValidSize = file.size <= maxSize;

    return hasValidExtension && isValidSize;
}

/**
 * Returns true if the file is an Excel file (.xlsx or .xls)
 * @param {File} file
 * @returns {boolean}
 */
export const isExcelFile = (file) => {
    if (!file) return false;
    const fileName = file.name.toLowerCase();
    return fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
}

/**
 * Parses an Excel file ArrayBuffer and returns array of objects (same shape as parseCSVContent)
 * @param {ArrayBuffer} arrayBuffer - Result of FileReader.readAsArrayBuffer
 * @returns {Array} Array of objects representing rows
 */
export const parseExcelFile = (arrayBuffer) => {
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    
    // Use header: 1 to get an array of arrays representing the raw cells
    const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    if (!rows || rows.length === 0) {
        return [];
    }

    // Find the first line that looks like a valid header row
    let headerIndex = -1;
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (!row || !Array.isArray(row) || row.length === 0) continue;

        const hasContent = row.some(cell => cell !== null && cell !== undefined && cell.toString().trim() !== '');
        if (hasContent) {
            headerIndex = i;
            break;
        }
    }

    if (headerIndex === -1 || headerIndex >= rows.length - 1) {
        return [];
    }

    const headers = rows[headerIndex].map(h => h !== null && h !== undefined ? h.toString().trim() : '');

    const data = [];
    for (let i = headerIndex + 1; i < rows.length; i++) {
        const row = rows[i];
        if (!row || !Array.isArray(row)) continue;

        // Skip completely empty rows
        const isRowEmpty = row.every(cell => cell === null || cell === undefined || cell.toString().trim() === '');
        if (isRowEmpty) continue;

        const rowObj = {};
        headers.forEach((header, index) => {
            rowObj[header] = row[index] !== null && row[index] !== undefined ? row[index].toString().trim() : '';
        });

        // Filter out rows that are entirely empty or consist only of unnamed/empty keys
        const shouldSkip = Object.entries(rowObj).every(([key, val]) => {
            const isValueEmpty = !val || val.toString().trim() === '';
            const normalizedKey = key.toLowerCase().replace(/[_\s\.]/g, '');
            const isKeyEmpty = !normalizedKey || normalizedKey.includes('empty');
            return isValueEmpty || isKeyEmpty;
        });

        if (!shouldSkip) {
            data.push(rowObj);
        }
    }

    return data;
}

/**
 * Parses CSV content and returns array of objects
 * @param {string} csvContent - CSV string content
 * @returns {Array} Array of objects representing CSV rows
 */
export const parseCSVContent = (csvContent) => {
    if (!csvContent || typeof csvContent !== 'string') {
        return [];
    }
    
    const lines = csvContent.trim().split('\n');
    if (lines.length < 2) {
        return []; // Need at least header and one data row
    }
    
    let headerIndex = -1;
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line || line === 'null' || line === 'undefined') {
            continue;
        }

        const cols = line.split(',').map(col => col.trim().replace(/["']/g, ''));
        const hasContent = cols.some(col => col !== '' && col !== null && col !== undefined);
        
        if (hasContent) {
            headerIndex = i;
            break;
        }
    }

    if (headerIndex === -1 || headerIndex >= lines.length - 1) {
        return []; // No data rows after the header
    }

    // Parse header row
    const headers = lines[headerIndex].split(',').map(header => header.trim().replace(/["']/g, ''));
    
    // Parse data rows starting from headerIndex + 1
    const data = [];
    for (let i = headerIndex + 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line || line === 'null' || line === 'undefined') {
            continue;
        }

        const values = lines[i].split(',').map(value => value.trim().replace(/["']/g, ''));
        
        if (values.length === headers.length) {
            const row = {};
            headers.forEach((header, index) => {
                row[header] = values[index];
            });
            data.push(row);
        }
    }
    
    return data;
}



export const openLink = (url) => {
    window.open(url, '_blank');
}

/**
 * Gets the user's country based on IP address (no user consent required)
 * @returns {Promise<Object>} Promise that resolves to { isInIndia: boolean, country: string, error?: string }
 */
export const getUserCountry = () => {
    return new Promise(async (resolve) => {
        try {
            // Try multiple free IP geolocation services for better reliability
            const services = [
                'https://ipapi.co/json/',
                'https://ip-api.com/json/',
                'https://api.country.is/'
            ];

            let country = 'Unknown';
            let serviceUsed = '';

            // Try each service until one works
            for (const service of services) {
                try {
                    const response = await fetch(service, {
                        method: 'GET',
                        headers: {
                            'Accept': 'application/json',
                        }
                    });

                    if (!response.ok) {
                        continue; // Try next service
                    }

                    const data = await response.json();
                    
                    // Parse country from different service response formats
                    if (data.country_name) {
                        country = data.country_name;
                        serviceUsed = 'ipapi.co';
                    } else if (data.country) {
                        country = data.country;
                        serviceUsed = 'ip-api.com';
                    } else if (data.country_code) {
                        // For country.is service, we need to map country code to name
                        country = getCountryNameFromCode(data.country_code);
                        serviceUsed = 'country.is';
                    }

                    if (country !== 'Unknown') {
                        break; // Success, stop trying other services
                    }
                } catch (error) {
                    continue; // Try next service
                }
            }

            // Check if it's India (handle different possible country name formats)
            const isInIndia = country.toLowerCase() === 'india' || 
                            country.toLowerCase() === 'in' ||
                            country.toLowerCase() === 'ind';

            resolve({
                isInIndia,
                country,
                serviceUsed: serviceUsed || 'none'
            });

        } catch (error) {
            resolve({
                isInIndia: false,
                country: 'Unknown',
                error: `Failed to determine country: ${error.message}`
            });
        }
    });
};

/**
 * Maps country codes to country names for services that only return codes
 * @param {string} countryCode - Two-letter country code
 * @returns {string} Country name
 */
const getCountryNameFromCode = (countryCode) => {
    const countryMap = {
        'IN': 'India',
        'US': 'United States',
        'GB': 'United Kingdom',
        'CA': 'Canada',
        'AU': 'Australia',
        'DE': 'Germany',
        'FR': 'France',
        'JP': 'Japan',
        'CN': 'China',
        'BR': 'Brazil',
        'RU': 'Russia',
        'IT': 'Italy',
        'ES': 'Spain',
        'MX': 'Mexico',
        'KR': 'South Korea',
        'NL': 'Netherlands',
        'SE': 'Sweden',
        'NO': 'Norway',
        'DK': 'Denmark',
        'FI': 'Finland',
        'CH': 'Switzerland',
        'AT': 'Austria',
        'BE': 'Belgium',
        'IE': 'Ireland',
        'PT': 'Portugal',
        'GR': 'Greece',
        'PL': 'Poland',
        'CZ': 'Czech Republic',
        'HU': 'Hungary',
        'RO': 'Romania',
        'BG': 'Bulgaria',
        'HR': 'Croatia',
        'SI': 'Slovenia',
        'SK': 'Slovakia',
        'LT': 'Lithuania',
        'LV': 'Latvia',
        'EE': 'Estonia',
        'CY': 'Cyprus',
        'MT': 'Malta',
        'LU': 'Luxembourg'
    };

    return countryMap[countryCode.toUpperCase()] || 'Unknown';
};

/**
 * Initiate WhatsApp chat
 */
export const initiateWhatsAppChat = () => {
    window.open('https://wa.me/917736315005', '_blank');
};

/**
 * Initiate Phone Call
 */
export const initiatePhoneCall = () => {
    window.open('tel:+917736315005', '_self');
};

/**
 * Initiate Email
 */
export const initiateEmail = () => {
    window.open('mailto:info@goodonesolutions.in', '_self');
};
